import { Button, Card, CardContent, CardHeader, CardTitle } from "@exhousing/design-system";
import { Banknote } from "lucide-react";
import { toast } from "sonner";

import {
  useOwnerPayouts,
  useOwnerStatement,
  useRecordPayout,
} from "@/hooks/api/useOwnerPayouts";
import { money } from "@/lib/money";

function firstOfMonth(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** What the agency owes this landlord from collected rent, and the button to settle it.
 *  Pays only what tenants have actually paid; each invoice is settled once. */
export function OwnerPayoutPanel({ landlordId }: { landlordId: string }) {
  const start = firstOfMonth();
  const end = today();
  const { data: stmt, isLoading } = useOwnerStatement(landlordId, start, end);
  const { data: payouts } = useOwnerPayouts(landlordId);
  const record = useRecordPayout(landlordId);

  if (isLoading || !stmt) return null;

  const net = stmt.net_to_owner;
  const count = stmt.invoices.length;

  const onPay = () =>
    record.mutate(
      { period_start: start, period_end: end },
      {
        onSuccess: (p) => toast.success(`Payout ${p.number} recorded — ${money(p.amount)}`),
        onError: (e) => toast.error(e.message),
      },
    );

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <Banknote className="size-4 text-accent" />
          Owner payouts
        </CardTitle>
        {net > 0 && (
          <Button size="sm" disabled={record.isPending} onClick={onPay}>
            {record.isPending ? "Recording…" : `Pay out ${money(net)}`}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {net > 0 ? (
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{money(net)}</span> ready to pay
            out, from {count} collected invoice{count === 1 ? "" : "s"}.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Nothing to pay out right now — settled up to today.
          </p>
        )}

        {net > 0 && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Collected" value={money(stmt.gross_collected)} />
            <Stat label="Commission" value={money(stmt.commission_total)} />
            <Stat label="Leasing fee" value={money(stmt.leasing_fee_deducted)} />
            <Stat label="Net to owner" value={money(net)} accent />
          </div>
        )}

        {payouts && payouts.length > 0 && (
          <div className="border-t border-border/60 pt-4">
            <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
              Payout history
            </p>
            <ul className="divide-y divide-border/60 text-sm">
              {payouts.map((p) => (
                <li key={p.id} className="flex items-center gap-3 py-2">
                  <span className="font-medium tabular-nums">{p.number}</span>
                  <span className="text-xs text-muted-foreground">
                    {p.period_start} → {p.period_end}
                  </span>
                  <span className="ml-auto tabular-nums">{money(p.amount)}</span>
                  <span className="w-24 text-right text-xs text-muted-foreground">
                    paid {p.paid_date}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-0.5 font-display text-lg tabular-nums${accent ? " text-accent" : ""}`}>
        {value}
      </p>
    </div>
  );
}
